/**
 * Team workspace: overview, members and invitations.
 *
 * Every control is gated on the capability map the backend sends with the overview,
 * so a Billing Admin never sees an "Invite" button that would 403. The server checks
 * again regardless — this is about not lying to the person, not about security.
 */

import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { roleLabels, type Member, type Role } from '@/entities/types'
import {
  useAccount,
  useClaimDomain,
  useCreateWorkspace,
  useDeleteWorkspace,
  useDomain,
  useExport,
  useInvite,
  useMembers,
  useOrganization,
  useRemoveMember,
  useRenameOrganization,
  useResendInvitation,
  useReleaseDomain,
  useRevokeInvitation,
  useTransferOwnership,
  useVerifyDomain,
  useUpdateMember,
} from '@/shared/api/hooks'
import { AppShell, PageHead } from '@/shared/ui/Chrome'
import {
  Badge,
  Button,
  ConfirmDialog,
  Empty,
  ErrorNotice,
  Field,
  Loading,
  Notice,
  Panel,
  Readout,
  Select,
  StatusBadge,
  formatDate,
  formatRelative,
} from '@/shared/ui/primitives'

/**
 * The workspace's domain, and the DNS record that proves it.
 *
 * On this page rather than under Enterprise because the claim is a property of the
 * workspace and free on every plan: it is the prerequisite for SSO and for domain
 * capture, and an admin who cannot prove who they are cannot evaluate either.
 * What the proof *unlocks* stays sold.
 */
function DomainPanel({ orgId }: { orgId: string }) {
  const { data: claim } = useDomain(orgId)
  const attach = useClaimDomain(orgId)
  const verify = useVerifyDomain(orgId)
  const release = useReleaseDomain(orgId)
  const [draft, setDraft] = useState('')

  const claimed = Boolean(claim?.name)

  return (
    <Panel
      title="Company domain"
      index="05"
      actions={
        claim?.verified ? (
          <Badge tone="signal">verified</Badge>
        ) : claimed ? (
          <Badge>awaiting DNS</Badge>
        ) : undefined
      }
    >
      <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
        {!claimed && (
          <form
            className="stack"
            style={{ ['--gap' as string]: 'var(--s4)' }}
            onSubmit={(event) => {
              event.preventDefault()
              attach.mutate(draft.trim())
            }}
          >
            <p className="dim" style={{ margin: 0, maxWidth: '62ch' }}>
              Prove that your company owns its email domain and Archis can route its
              sign-ins through your identity provider and add its people to this workspace
              automatically. Proving it is a DNS record; what it unlocks is part of
              Enterprise.
            </p>
            <Field label="Domain" hint="The bare domain your addresses are under — acme.com, not an address.">
              <input
                className="input"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder="acme.com"
              />
            </Field>
            <ErrorNotice error={attach.error} />
            <div className="row">
              <Button type="submit" variant="primary" loading={attach.isPending}>
                Claim domain
              </Button>
            </div>
          </form>
        )}

        {claimed && claim && (
          <>
            <div className="row row--between" style={{ gap: 'var(--s4)', flexWrap: 'wrap' }}>
              <div>
                <Readout value={claim.name} caption="claimed domain" />
              </div>
              {claim.verified && claim.verified_at && (
                <p className="mono-note" style={{ margin: 0 }}>
                  Verified {formatDate(claim.verified_at)}.
                  {claim.capture_enabled
                    ? ' Capture is on: verified addresses under it join this workspace.'
                    : ''}
                </p>
              )}
            </div>

            {!claim.verified && (
              <>
                <p className="dim" style={{ margin: 0, maxWidth: '62ch' }}>
                  Add this TXT record at your DNS provider, then verify. Nothing changes for
                  your mail — the record only proves control of the domain.
                </p>
                <div className="stack" style={{ ['--gap' as string]: 'var(--s2)' }}>
                  <Field label="Record name">
                    <input className="input" value={claim.record_name ?? ''} readOnly />
                  </Field>
                  <Field label="Record value">
                    <input className="input" value={claim.record_value ?? ''} readOnly />
                  </Field>
                </div>
                {claim.check_error && (
                  <Notice tone="warn">
                    Last check: {claim.check_error}
                    {claim.checked_at ? ` (${formatRelative(claim.checked_at)})` : ''}
                  </Notice>
                )}
              </>
            )}

            <ErrorNotice error={verify.error} />
            <ErrorNotice error={release.error} />
            {verify.isSuccess && claim.verified && (
              <Notice tone="ok">Domain verified. SSO can now route it.</Notice>
            )}

            <div className="row" style={{ gap: 'var(--s3)' }}>
              {!claim.verified && (
                <Button variant="primary" loading={verify.isPending} onClick={() => verify.mutate()}>
                  Verify domain
                </Button>
              )}
              <Button
                variant="ghost"
                loading={release.isPending}
                disabled={claim.used_by_sso}
                onClick={() => release.mutate()}
              >
                Release domain
              </Button>
              {claim.used_by_sso && (
                <span className="mono-note">
                  Single sign-on is routing this domain — turn SSO off first.
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </Panel>
  )
}

/** Resolves the workspace this person administers, or null for a Pro account. */
export function useCurrentOrgId(): string | undefined {
  const { data: account } = useAccount()
  return (account?.organizations.find((org) => org.has_seat) ?? account?.organizations[0])?.id
}

/**
 * The empty state, and the only place a workspace is created from.
 *
 * It leads with creation rather than with pricing because the first two seats are
 * free: the workspace is what puts an organization-owned repository in scope, so
 * "create one" is the answer to why somebody is on this page, and Team is the
 * answer to the third person joining.
 */
export function NoWorkspace() {
  const create = useCreateWorkspace()
  const [name, setName] = useState('')

  return (
    <AppShell>
      <PageHead
        index="05"
        eyebrow="Workspace"
        title="No workspace"
        subtitle="A workspace is what puts your company's repositories in scope. The first two seats are free."
      />
      <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
        <Panel title="Create a workspace" index="01" tone="signal">
          <form
            className="stack"
            style={{ ['--gap' as string]: 'var(--s4)' }}
            onSubmit={(event) => {
              event.preventDefault()
              create.mutate(name.trim())
            }}
          >
            <p className="dim" style={{ margin: 0, maxWidth: '62ch' }}>
              Two seats, no card: you and one other engineer can index repositories your
              organization owns, invite each other, and hold a seat each. Seats you assign
              and reassign, roles, a per-member device limit and an offline window, and the audit log come with Team.
            </p>
            <Field label="Workspace name" hint="Your company or team. You can rename it later.">
              <input
                className="input"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Acme Robotics"
              />
            </Field>
            <ErrorNotice error={create.error} />
            <div className="row">
              <Button type="submit" variant="primary" loading={create.isPending}>
                Create workspace
              </Button>
              <Link to="/pricing">
                <Button type="button" variant="ghost">
                  Compare plans
                </Button>
              </Link>
            </div>
          </form>
        </Panel>
      </div>
    </AppShell>
  )
}

export function Team() {
  const orgId = useCurrentOrgId()
  if (!orgId) return <NoWorkspace />
  return <TeamWorkspace orgId={orgId} />
}

function TeamWorkspace({ orgId }: { orgId: string }) {
  const { data: org, isPending, error } = useOrganization(orgId)
  const { data: roster } = useMembers(orgId)
  const exportCsv = useExport(orgId)

  const invite = useInvite(orgId)
  const revokeInvite = useRevokeInvitation(orgId)
  const resendInvite = useResendInvitation(orgId)
  const updateMember = useUpdateMember(orgId)
  const removeMember = useRemoveMember(orgId)
  const transfer = useTransferOwnership(orgId)
  const rename = useRenameOrganization(orgId)
  const deleteWorkspace = useDeleteWorkspace(orgId)
  const { data: account } = useAccount()
  const navigate = useNavigate()

  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<Role>('member')
  const [roleChanging, setRoleChanging] = useState<{ member: Member; newRole: Role } | null>(null)
  const [suspending, setSuspending] = useState<Member | null>(null)
  const [suspendAgreed, setSuspendAgreed] = useState(false)
  const [removing, setRemoving] = useState<Member | null>(null)
  const [removeAgreed, setRemoveAgreed] = useState(false)
  const [transferring, setTransferring] = useState<Member | null>(null)
  const [transferConfirm, setTransferConfirm] = useState('')
  const [renameDraft, setRenameDraft] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState('')

  const can = org?.capabilities ?? {}
  const isOwner = org?.role === 'owner'
  const isMember = org?.role === 'member'
  const isFreeEvaluation = org?.plan === 'free_team' || !org?.subscription_status
  const currentOrgRef = account?.organizations?.find((o) => o.id === orgId)
  const hasSeat = currentOrgRef?.has_seat ?? false

  return (
    <AppShell>
      <PageHead
        index="05"
        eyebrow="Workspace"
        title={org?.name ?? 'Workspace'}
        subtitle={isMember ? 'Team directory and your workspace seat.' : 'Members, roles and invitations.'}
        actions={
          <>
            {can['organization.rename'] && (
              <Button size="sm" variant="ghost" onClick={() => setRenameDraft(org?.name ?? '')}>
                Rename
              </Button>
            )}
            {can['members.manage'] && (
              <Button
                size="sm"
                loading={exportCsv.isPending}
                onClick={() => exportCsv.mutate('members')}
              >
                Export CSV
              </Button>
            )}
          </>
        }
      />

      {isPending && <Loading label="Loading workspace" />}
      {error ? (
        <Panel tone="coral" title="Workspace Access Unavailable" index="!">
          <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
            <p style={{ margin: 0 }}>
              {error.message?.toLowerCase().includes('suspended')
                ? 'Your membership in this workspace has been suspended by an administrator.'
                : error.message || 'Unable to access this workspace.'}
            </p>
            <p className="dim" style={{ margin: 0 }}>
              You do not have permission to view team members, settings, or access organization repositories.
              Please contact your workspace Owner or Admin.
            </p>
            <div className="row">
              <Link to="/account">
                <Button size="sm">Back to Account</Button>
              </Link>
            </div>
          </div>
        </Panel>
      ) : null}

      {org && (
        <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
          {org.pilot && (
            <Panel tone="signal" title="Enterprise Pilot Active" index="!">
              <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
                <p style={{ margin: 0 }} data-testid="pilot-countdown">
                  <strong>
                    {org.pilot.days_left} {org.pilot.days_left === 1 ? 'day' : 'days'} remaining
                  </strong>{' '}
                  · {org.seats.assigned}/{org.seats.purchased} seats assigned · ends{' '}
                  {formatDate(org.pilot.ends_at)}
                </p>
                <p className="dim" style={{ margin: 0, maxWidth: '62ch' }}>
                  Every Enterprise capability — SSO, SCIM, audit streaming, offline
                  licences — is on for the pilot. When it ends the workspace falls back
                  to its two included seats; nothing on anyone's machine changes.
                </p>
                {can['billing.manage'] && (
                  <div className="row" style={{ gap: 'var(--s3)', flexWrap: 'wrap' }}>
                    <Link to="/enterprise">
                      <Button size="sm" variant="primary">Talk to us about an annual plan</Button>
                    </Link>
                    <Link to="/team/usage">
                      <Button size="sm" variant="ghost">See what the pilot saved</Button>
                    </Link>
                  </div>
                )}
              </div>
            </Panel>
          )}
          {isMember ? (
            <div className="readout-grid">
              <div>
                <Readout
                  value={isFreeEvaluation ? 'TEAM FREE' : org.plan.toUpperCase()}
                  caption="workspace tier"
                  tone="signal"
                />
              </div>
              <div>
                <Readout
                  value={hasSeat ? 'ASSIGNED' : 'UNSEATED'}
                  caption="your seat"
                  tone={hasSeat ? 'signal' : 'amber'}
                />
              </div>
              <div>
                <Readout
                  value={org.members}
                  caption="team members"
                />
              </div>
              <div>
                <Readout
                  value={hasSeat ? 'ACTIVE' : 'PENDING'}
                  caption="seat allocation"
                  tone={hasSeat ? 'signal' : undefined}
                />
              </div>
            </div>
          ) : (
            <div className="readout-grid">
              <div>
                <Readout
                  value={org.seats.purchased}
                  caption={isFreeEvaluation ? 'free seats' : 'seats purchased'}
                />
              </div>
              <div>
                <Readout
                  value={org.seats.assigned}
                  caption="assigned"
                  tone={org.seats.available === 0 ? 'amber' : 'signal'}
                />
              </div>
              <div>
                <Readout value={org.seats.reserved} caption="reserved by invite" />
              </div>
              <div>
                <div className="readout">
                  <div style={{ paddingBlock: '0.35rem' }}>
                    {org.subscription_status ? (
                      <StatusBadge status={org.subscription_status} />
                    ) : (
                      <Badge tone="signal">2 Free Seats</Badge>
                    )}
                  </div>
                  <div className="readout__caption">{isFreeEvaluation ? 'evaluation' : 'subscription'}</div>
                </div>
              </div>
            </div>
          )}

          {isMember && (
            <Panel title={hasSeat ? 'CLI authentication' : 'Seat allocation pending'} index="01">
              <div className="stack" style={{ ['--gap' as string]: 'var(--s3)' }}>
                <p style={{ margin: 0 }}>
                  {hasSeat ? (
                    <>
                      You hold an active seat in <strong>{org.name}</strong>. Authenticate your terminal to enable Archis semantic understanding on your team's codebases.
                    </>
                  ) : (
                    <>
                      You are an active member of <strong>{org.name}</strong>, but a seat has not been allocated to your account yet. Contact your workspace administrator to allocate an available seat so your CLI can access organization repositories.
                    </>
                  )}
                </p>
                {hasSeat && (
                  <div className="term">
                    <div className="term__bar">terminal</div>
                    <div className="term__body">
                      <div className="term__line"><span className="term__prompt">$</span> chi auth login</div>
                    </div>
                  </div>
                )}
              </div>
            </Panel>
          )}

          {/* Invite. Only rendered when the caller's role allows it. */}
          {can['members.manage'] && (
            <Panel title="Invite a member" index="03">
              <form
                className="stack"
                style={{ ['--gap' as string]: 'var(--s4)' }}
                onSubmit={(event) => {
                  event.preventDefault()
                  invite.mutate(
                    { email: inviteEmail.trim(), role: inviteRole },
                    { onSuccess: () => setInviteEmail('') },
                  )
                }}
              >
                <div className="grid-2" style={{ gap: 'var(--s4)' }}>
                  <Field
                    label="Email address"
                    hint="An invitation reserves a seat for 7 days."
                  >
                    <input
                      className="input"
                      type="email"
                      required
                      value={inviteEmail}
                      onChange={(event) => setInviteEmail(event.target.value)}
                      placeholder="developer@company.com"
                    />
                  </Field>
                  <Field label="Role">
                    <Select
                      value={inviteRole}
                      onChange={(event) => setInviteRole(event.target.value as Role)}
                      options={[
                        { value: 'member', label: roleLabels.member },
                        { value: 'admin', label: roleLabels.admin },
                        { value: 'billing_admin', label: roleLabels.billing_admin },
                      ]}
                    />
                  </Field>
                </div>

                <ErrorNotice error={invite.error} />
                {invite.isSuccess && <Notice tone="ok">Invitation sent.</Notice>}

                {org.seats.available === 0 && isFreeEvaluation ? (
                  can['billing.manage'] ? (
                    <Notice tone="warn">
                      Both included free evaluation seats are in use. To invite a 3rd member and unlock shared policies and audit logs,{' '}
                      <Link to="/billing" className="signal" style={{ fontWeight: 600 }}>
                        upgrade to Archis Team (3+ seats) →
                      </Link>
                    </Notice>
                  ) : (
                    <Notice tone="warn">
                      Both included free evaluation seats are in use. Contact your workspace Owner or Billing Admin to increase seat capacity.
                    </Notice>
                  )
                ) : org.seats.available === 0 ? (
                  can['billing.manage'] ? (
                    <Notice tone="warn">
                      No seats are free. <Link to="/billing" className="signal">Buy more seats</Link> or revoke one before inviting — an
                      invitation reserves a seat as soon as it is sent.
                    </Notice>
                  ) : (
                    <Notice tone="warn">
                      No seats are free. Contact your workspace Owner or Billing Admin to add seat capacity or revoke an unused seat.
                    </Notice>
                  )
                ) : null}

                <div className="row" style={{ gap: 'var(--s3)' }}>
                  <Button
                    type="submit"
                    variant="primary"
                    loading={invite.isPending}
                    disabled={org.seats.available === 0}
                  >
                    Send invitation
                  </Button>
                  {org.seats.available === 0 && isFreeEvaluation && can['billing.manage'] && (
                    <Link to="/billing">
                      <Button type="button" variant="primary">
                        Upgrade to Archis Team (3+ seats) →
                      </Button>
                    </Link>
                  )}
                </div>
              </form>
            </Panel>
          )}

          {/* Pending invitations. */}
          {roster && roster.invitations.length > 0 && (
            <Panel title="Pending invitations" index="04" padded={false}>
              <ErrorNotice error={resendInvite.error} />
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Expires</th>
                      <th className="actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {roster.invitations.map((invitation) => (
                      <tr key={invitation.id}>
                        <td>{invitation.email}</td>
                        <td>
                          <Badge>{roleLabels[invitation.role]}</Badge>
                        </td>
                        <td className="mono-note">{formatRelative(invitation.expires_at)}</td>
                        <td className="actions">
                          {can['members.manage'] && (
                            <div
                              className="row"
                              style={{ justifyContent: 'flex-end', gap: 'var(--s1)' }}
                            >
                              <Button
                                size="sm"
                                variant="ghost"
                                loading={resendInvite.isPending}
                                onClick={() => resendInvite.mutate(invitation.id)}
                              >
                                Resend
                              </Button>
                              <Button
                                size="sm"
                                variant="danger"
                                loading={revokeInvite.isPending}
                                onClick={() => revokeInvite.mutate(invitation.id)}
                              >
                                Revoke
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          )}

          {/* Members. */}
          <Panel title="Members" index="05" padded={false}>
            <ErrorNotice error={updateMember.error} />
            {!roster && <Loading label="Loading members" />}
            {roster && roster.members.length === 0 && <Empty>No members yet.</Empty>}

            {roster && roster.members.length > 0 && (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Member</th>
                      <th>Role</th>
                      <th>Seat</th>
                      <th>Status</th>
                      <th>Joined</th>
                      <th className="actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {roster.members
                      .filter((member) => member.status !== 'removed')
                      .map((member) => {
                        const isSelf = (member.user_id && member.user_id === account?.id) || member.email === account?.email
                        const canManageThisMember = can['members.manage'] && !isSelf && member.role !== 'owner' && (isOwner || member.role === 'member')

                        return (
                          <tr key={member.id}>
                            <td>
                              <div className="row" style={{ gap: 'var(--s2)', alignItems: 'center' }}>
                                <span>{member.display_name || member.email}</span>
                                {isSelf && <Badge tone="signal">You</Badge>}
                              </div>
                              <div className="mono-note">{member.email}</div>
                            </td>
                            <td>
                              {/* The owner's role is not editable here: ownership moves
                                  only through an explicit transfer. Administrative roles
                                  can only be edited by the workspace owner. */}
                              {canManageThisMember ? (
                                <Select
                                  value={member.role}
                                  onChange={(event) =>
                                    setRoleChanging({
                                      member,
                                      newRole: event.target.value as Role,
                                    })
                                  }
                                  options={
                                    isOwner
                                      ? [
                                          { value: 'member', label: roleLabels.member },
                                          { value: 'admin', label: roleLabels.admin },
                                          { value: 'billing_admin', label: roleLabels.billing_admin },
                                        ]
                                      : [
                                          { value: 'member', label: roleLabels.member },
                                          { value: 'admin', label: roleLabels.admin },
                                        ]
                                  }
                                  style={{ maxWidth: 150 }}
                                />
                              ) : (
                                <Badge tone={member.role === 'owner' ? 'signal' : undefined}>
                                  {roleLabels[member.role]}
                                </Badge>
                              )}
                            </td>
                            <td>
                              {member.has_seat ? (
                                <Badge tone="signal" dot>
                                  assigned
                                </Badge>
                              ) : (
                                <Badge tone="amber">none</Badge>
                              )}
                            </td>
                            <td>
                              <StatusBadge status={member.status} />
                            </td>
                            <td className="mono-note">
                              {formatDate(member.joined_at)}
                              {member.last_active_at ? (
                                <div className="dim">active {formatDate(member.last_active_at)}</div>
                              ) : null}
                            </td>
                            <td className="actions">
                              {can['members.manage'] && (
                                <div
                                  className="row"
                                  style={{ justifyContent: 'flex-end', gap: 'var(--s1)' }}
                                >
                                  {canManageThisMember && (
                                    <>
                                      {member.status === 'active' ? (
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => {
                                            setSuspending(member)
                                            setSuspendAgreed(false)
                                          }}
                                        >
                                          Suspend
                                        </Button>
                                      ) : (
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          loading={updateMember.isPending}
                                          onClick={() =>
                                            updateMember.mutate({ memberId: member.id, status: 'active' })
                                          }
                                        >
                                          Reactivate
                                        </Button>
                                      )}
                                    </>
                                  )}
                                  {can['organization.transfer'] && member.role !== 'owner' && !isSelf && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => setTransferring(member)}
                                    >
                                      Make owner
                                    </Button>
                                  )}
                                  {canManageThisMember && (
                                    <Button
                                      size="sm"
                                      variant="danger"
                                      onClick={() => {
                                        setRemoving(member)
                                        setRemoveAgreed(false)
                                      }}
                                    >
                                      Remove
                                    </Button>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          {can['policies.manage'] && <DomainPanel orgId={org.id} />}

          <p className="mono-note">
            Roles: <strong>Owner</strong> controls everything including transfer and deletion.{' '}
            <strong>Admin</strong> manages members, seats, devices and policies.{' '}
            <strong>Billing admin</strong> manages the subscription and seat quantity.{' '}
            <strong>Member</strong> consumes a seat and uses Archis.
          </p>

          {/* Deletion. Owner only, and the backend demands the typed name again. */}
          {can['organization.delete'] && (
            <Panel title="Danger zone" index="06" tone="coral">
              <div className="row row--between" style={{ gap: 'var(--s4)', flexWrap: 'wrap' }}>
                <p className="dim" style={{ margin: 0, maxWidth: '52ch' }}>
                  Deleting the workspace ends every member's access through it and revokes
                  their organization devices. An active subscription must be cancelled in
                  the billing portal first.
                </p>
                <Button variant="danger" onClick={() => setDeleting(true)}>
                  Delete workspace
                </Button>
              </div>
            </Panel>
          )}
        </div>
      )}

      {roleChanging && (
        <ConfirmDialog
          title={`Change role: ${roleChanging.member.display_name || roleChanging.member.email}`}
          confirmLabel={`Change to ${roleLabels[roleChanging.newRole]}`}
          pending={updateMember.isPending}
          onCancel={() => setRoleChanging(null)}
          onConfirm={() =>
            updateMember.mutate(
              { memberId: roleChanging.member.id, role: roleChanging.newRole },
              { onSuccess: () => setRoleChanging(null) },
            )
          }
          body={
            <div className="stack" style={{ ['--gap' as string]: 'var(--s3)' }}>
              <p style={{ margin: 0 }}>
                You are changing <strong>{roleChanging.member.display_name || roleChanging.member.email}</strong> from{' '}
                <strong>{roleLabels[roleChanging.member.role]}</strong> to <strong>{roleLabels[roleChanging.newRole]}</strong>.
              </p>
              {roleChanging.newRole === 'admin' && (
                <Notice tone="warn">
                  <span>
                    <strong>Administrative access granted</strong>: This member will be able to invite colleagues,
                    assign seats, manage developer security policies, and oversee member devices.
                  </span>
                </Notice>
              )}
              {roleChanging.newRole === 'billing_admin' && (
                <Notice tone="warn">
                  <span>
                    <strong>Billing authority granted</strong>: This member will have full access to manage Stripe
                    subscriptions, view invoice history, and adjust seat quantities.
                  </span>
                </Notice>
              )}
              {roleChanging.newRole === 'member' && (
                <p className="dim" style={{ margin: 0 }}>
                  This member will lose all workspace administrative privileges and will solely consume an assigned
                  seat to use the CLI.
                </p>
              )}
              <ErrorNotice error={updateMember.error} />
            </div>
          }
        />
      )}

      {suspending && (
        <ConfirmDialog
          title={`Suspend access: ${suspending.display_name || suspending.email}`}
          danger
          confirmLabel="Suspend member"
          confirmDisabled={!suspendAgreed}
          pending={updateMember.isPending}
          onCancel={() => {
            setSuspending(null)
            setSuspendAgreed(false)
          }}
          onConfirm={() =>
            updateMember.mutate(
              { memberId: suspending.id, status: 'suspended' },
              {
                onSuccess: () => {
                  setSuspending(null)
                  setSuspendAgreed(false)
                },
              },
            )
          }
          body={
            <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
              <p style={{ margin: 0 }}>
                Suspending <strong>{suspending.display_name || suspending.email}</strong> temporarily pauses their access to this workspace:
              </p>
              <ul style={{ margin: 0, paddingLeft: '1.2rem', display: 'flex', flexDirection: 'column', gap: 'var(--s2)' }}>
                <li><strong>Immediate token revocation</strong>: All active CLI tokens and registered organization devices are terminated.</li>
                <li><strong>Repository lockout</strong>: Access to company repositories and private semantic graphs is severed immediately.</li>
                <li><strong>Seat retention</strong>: Their seat reservation remains assigned in this workspace until explicitly unseated or removed.</li>
              </ul>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--s2)', cursor: 'pointer', marginTop: 'var(--s2)' }}>
                <input
                  type="checkbox"
                  checked={suspendAgreed}
                  onChange={(e) => setSuspendAgreed(e.target.checked)}
                  style={{ marginTop: '0.2rem' }}
                />
                <span style={{ fontSize: '0.9rem' }}>
                  I understand this will immediately disconnect this user from Archis CLI and team repositories.
                </span>
              </label>
              <ErrorNotice error={updateMember.error} />
            </div>
          }
        />
      )}

      {removing && (
        <ConfirmDialog
          title={`Remove member: ${removing.display_name || removing.email}`}
          danger
          confirmLabel="Remove member"
          confirmDisabled={!removeAgreed}
          pending={removeMember.isPending}
          onCancel={() => {
            setRemoving(null)
            setRemoveAgreed(false)
          }}
          onConfirm={() =>
            removeMember.mutate(removing.id, {
              onSuccess: () => {
                setRemoving(null)
                setRemoveAgreed(false)
              },
            })
          }
          body={
            <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
              <p style={{ margin: 0 }}>
                <strong>{removing.display_name || removing.email}</strong> will be permanently removed from <strong>{org?.name}</strong>:
              </p>
              <ul style={{ margin: 0, paddingLeft: '1.2rem', display: 'flex', flexDirection: 'column', gap: 'var(--s2)' }}>
                <li><strong>Seat released</strong>: Their assigned seat will be returned to the workspace available pool.</li>
                <li><strong>Access severed</strong>: All organization CLI devices and session leases are revoked.</li>
                <li><strong>Local source preserved</strong>: Files on the member's physical machine are unaffected.</li>
              </ul>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--s2)', cursor: 'pointer', marginTop: 'var(--s2)' }}>
                <input
                  type="checkbox"
                  checked={removeAgreed}
                  onChange={(e) => setRemoveAgreed(e.target.checked)}
                  style={{ marginTop: '0.2rem' }}
                />
                <span style={{ fontSize: '0.9rem' }}>
                  I agree to remove this member and release their seat back to the organization.
                </span>
              </label>
              <ErrorNotice error={removeMember.error} />
            </div>
          }
        />
      )}

      {transferring && (
        <ConfirmDialog
          title="Transfer ownership"
          danger
          confirmLabel="Transfer ownership"
          pending={transfer.isPending}
          confirmDisabled={transferConfirm.trim() !== (org?.name ?? '')}
          onCancel={() => {
            setTransferring(null)
            setTransferConfirm('')
          }}
          onConfirm={() =>
            transfer.mutate(
              { memberId: transferring.id, confirmName: transferConfirm },
              {
                onSuccess: () => {
                  setTransferring(null)
                  setTransferConfirm('')
                },
              },
            )
          }
          body={
            <>
              <p style={{ margin: 0 }}>
                <strong>{transferring.email}</strong> becomes the Owner of this workspace. You
                become an Admin.
              </p>
              <p className="mono-note" style={{ margin: 0 }}>
                Only an Owner can transfer ownership or delete the workspace. You cannot undo
                this yourself afterwards.
              </p>
              <Field label={`Type "${org?.name ?? ''}" to confirm`}>
                <input
                  className="input"
                  value={transferConfirm}
                  onChange={(event) => setTransferConfirm(event.target.value)}
                  autoFocus
                />
              </Field>
              <ErrorNotice error={transfer.error} />
            </>
          }
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Delete workspace"
          danger
          confirmLabel="Delete workspace"
          pending={deleteWorkspace.isPending}
          confirmDisabled={deleteConfirm.trim() !== (org?.name ?? '')}
          onCancel={() => {
            setDeleting(false)
            setDeleteConfirm('')
          }}
          onConfirm={() =>
            deleteWorkspace.mutate(deleteConfirm, {
              onSuccess: () => navigate('/account'),
            })
          }
          body={
            <>
              <p style={{ margin: 0 }}>
                The workspace, its seats, its policies and its audit history stop being
                reachable. Every member's organization devices are revoked and their access
                through this workspace ends.
              </p>
              <p className="mono-note" style={{ margin: 0 }}>
                Nothing on anyone's machine is deleted — indexes and graphs are local. This
                cannot be undone.
              </p>
              <Field label={`Type "${org?.name ?? ''}" to confirm`}>
                <input
                  className="input"
                  value={deleteConfirm}
                  onChange={(event) => setDeleteConfirm(event.target.value)}
                  autoFocus
                />
              </Field>
              <ErrorNotice error={deleteWorkspace.error} />
            </>
          }
        />
      )}

      {renameDraft !== null && (
        <ConfirmDialog
          title="Rename workspace"
          confirmLabel="Save"
          pending={rename.isPending}
          onCancel={() => setRenameDraft(null)}
          onConfirm={() =>
            rename.mutate(renameDraft, { onSuccess: () => setRenameDraft(null) })
          }
          body={
            <Field label="Workspace name">
              <input
                className="input"
                value={renameDraft}
                onChange={(event) => setRenameDraft(event.target.value)}
                maxLength={80}
                autoFocus
              />
            </Field>
          }
        />
      )}
    </AppShell>
  )
}
